import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ConfirmPasswordModal } from '@/components/ConfirmPasswordModal'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { StatusPill } from '@/components/ui/StatusPill'
import { useToast } from '@/components/ui/toastContext'
import type { LmsSource } from '@/features/admin/api/lmsSources.api'
import {
  useCreateLmsSource,
  useLmsSourcesQuery,
  useRegenerateLmsSourceSecret,
  useSetLmsSourceActive,
} from '@/features/admin/hooks/useLmsSources'
import { usePasswordConfirmGate } from '@/hooks/usePasswordConfirmGate'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { formatDate } from '@/utils/format'

const createSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(50)
    .regex(/^[a-z0-9_-]+$/i, 'Use only letters, numbers, hyphens and underscores'),
  name: z.string().min(1, 'Name is required').max(255),
  base_url: z.union([z.url('Enter a valid URL'), z.literal('')]).optional(),
  contact_email: z.union([z.email('Enter a valid email address'), z.literal('')]).optional(),
})

type CreateFormValues = z.infer<typeof createSchema>

/**
 * The only way to issue API credentials for a.lms.com/b.lms.com/
 * c.lms.com (or any future LMS) without a raw DB insert — see
 * VerifyLmsSignature for how these keys get used. Every mutating action
 * here is password.confirm-gated on the backend, same bar as blocking a
 * user account.
 */
export function LmsSourcesPage() {
  const { data: sources, isPending } = useLmsSourcesQuery()
  const create = useCreateLmsSource()
  const setActive = useSetLmsSourceActive()
  const regenerateSecret = useRegenerateLmsSourceSecret()
  const toast = useToast()
  const gate = usePasswordConfirmGate()

  const [showCreate, setShowCreate] = useState(false)
  const [createError, setCreateError] = useState<unknown>(null)
  const [revealedSecret, setRevealedSecret] = useState<{ name: string; secret: string } | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { code: '', name: '', base_url: '', contact_email: '' },
  })

  const onCreate = handleSubmit((values) => {
    setCreateError(null)
    create.mutate(
      {
        code: values.code,
        name: values.name,
        base_url: values.base_url || undefined,
        contact_email: values.contact_email || undefined,
      },
      {
        onSuccess: (result) => {
          reset()
          setShowCreate(false)
          setRevealedSecret({ name: result.source.name, secret: result.plainSecret })
        },
        onError: (error) => {
          if (!gate.guardedError(error, () => void onCreate())) {
            setCreateError(error)
          }
        },
      },
    )
  })

  const toggleActive = (source: LmsSource) => {
    const retry = () => toggleActive(source)
    setActive.mutate(
      { id: source.id, active: !source.isActive },
      {
        onError: (error) => {
          if (!gate.guardedError(error, retry)) {
            toast.error(getErrorMessage(error))
          }
        },
      },
    )
  }

  const regenerate = (source: LmsSource) => {
    const retry = () => regenerate(source)
    regenerateSecret.mutate(source.id, {
      onSuccess: (result) => setRevealedSecret({ name: result.source.name, secret: result.plainSecret }),
      onError: (error) => {
        if (!gate.guardedError(error, retry)) {
          toast.error(getErrorMessage(error))
        }
      },
    })
  }

  const columns: DataTableColumn<LmsSource>[] = [
    { key: 'name', header: 'Name', render: (row) => row.name },
    { key: 'code', header: 'Code', render: (row) => <code className="fs-12">{row.code}</code> },
    { key: 'apiKey', header: 'API Key', render: (row) => <code className="fs-12">{row.apiKey}</code> },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={row.isActive ? 'active' : 'inactive'} />,
    },
    { key: 'profiles', header: 'Candidates', render: (row) => row.studentProfileCount ?? 0 },
    { key: 'created', header: 'Created', render: (row) => formatDate(row.createdAt) },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.code === 'portal' ? (
          <span className="fs-12 text-muted">Native portal</span>
        ) : (
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={() => regenerate(row)} loading={regenerateSecret.isPending}>
              Regenerate secret
            </Button>
            <Button
              variant={row.isActive ? 'danger' : 'secondary'}
              confirmTitle={row.isActive ? 'Deactivate this LMS integration?' : undefined}
              skipConfirm={!row.isActive}
              onClick={() => toggleActive(row)}
              loading={setActive.isPending}
            >
              {row.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        ),
      className: 'text-end',
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-0">LMS Integrations</p>
          <p className="text-muted fs-13 mb-0">
            Manage the external LMS platforms that push candidate profiles and applications via API.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>Add LMS Source</Button>
      </div>

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={sources ?? []}
          rowKey={(row) => row.id}
          loading={isPending}
          emptyMessage="No LMS sources yet."
        />
      </div>

      <Modal show={showCreate} onClose={() => setShowCreate(false)} title="Add LMS Source">
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(createError)}
          generalMessage={createError ? getErrorMessage(createError) : null}
        />
        <form onSubmit={(e) => void onCreate(e)} noValidate>
          <FormInput control={control} name="name" label="Display Name" placeholder="a.lms.com" required />
          <FormInput control={control} name="code" label="Code" placeholder="lms_a" required />
          <FormInput control={control} name="base_url" label="Base URL" placeholder="https://a.lms.com" />
          <FormInput control={control} name="contact_email" label="Contact Email" type="email" />
          <Button type="submit" className="w-100" loading={create.isPending}>
            Create
          </Button>
        </form>
      </Modal>

      <Modal
        show={revealedSecret != null}
        onClose={() => setRevealedSecret(null)}
        title="Copy the secret now"
        footer={
          <Button onClick={() => setRevealedSecret(null)}>Done</Button>
        }
      >
        {revealedSecret && (
          <>
            <p className="fs-13 text-muted">
              This is the only time <strong>{revealedSecret.name}</strong>&apos;s secret will be shown. Hand it to
              the integration owner now — it can&apos;t be retrieved again, only regenerated (which invalidates
              this one).
            </p>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control font-monospace fs-12"
                value={revealedSecret.secret}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(revealedSecret.secret)
                  toast.success('Secret copied to clipboard.')
                }}
              >
                Copy
              </Button>
            </div>
          </>
        )}
      </Modal>

      <ConfirmPasswordModal show={gate.show} onClose={() => gate.setShow(false)} onConfirmed={gate.handleConfirmed} />
    </>
  )
}
